import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, ShieldCheck, FileText } from "lucide-react";
import InvestorShell from "./InvestorShell";
import { api, money, Badge } from "./api";

const pretty = (s) => (s || "").replace(/_/g, " ");

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const [v, setV] = useState(null);
  const [gates, setGates] = useState(null);
  const load = useCallback(() => {
    api.get(`/investor/vehicles/${vehicleId}`).then(({ data }) => setV(data)).catch((e) => {
      toast.error(e?.response?.data?.detail || "Could not load vehicle");
      setV({ error: true });
    });
    api.get("/investor/me").then(({ data }) => setGates(data.gates)).catch(() => {});
  }, [vehicleId]);
  useEffect(() => { load(); }, [load]);

  const indicate = async (tier_pct) => {
    if (!window.confirm(`Record non-binding interest at the proposed ${tier_pct}% tier? This is non-binding and creates no obligation. Xtreme Exotics will be notified.`)) return;
    try {
      await api.post("/investor/interests", { vehicle_id: vehicleId, tier_pct });
      toast.success("Non-binding interest recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record interest"); }
  };

  if (!v) return <InvestorShell title="Vehicle"><div className="py-20 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></div></InvestorShell>;
  if (v.error) return <InvestorShell title="Vehicle"><p className="py-20 text-center text-sm text-muted">Vehicle not available. <Link to="/investors/portal" className="text-red-700 hover:underline">Back to dashboard</Link></p></InvestorShell>;

  const statusRows = [
    ["Current diligence status", v.diligence_status],
    ["Proposed location status", v.proposed_location_status],
    ["Host operator status", v.host_operator_status],
    ["Insurance status", v.insurance_status],
    ["Permit status", v.permit_status],
    ["Purchase order status", v.purchase_order_status],
  ];

  return (
    <InvestorShell gates={gates} title={v.name} subtitle={`Internal ID ${v.internal_vehicle_id || "—"} · Acquisition cost ${money(v.acquisition_cost)}`}>
      <div className="grid gap-6 lg:grid-cols-3" data-testid="vehicle-detail">
        <div className="space-y-6 lg:col-span-2">
          {/* funding tiers */}
          <div className="card-line rounded-xl bg-white p-6" data-testid="vehicle-tiers">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-base font-extrabold text-offwhite">Proposed funding tiers</h2>
              <Badge value={v.availability} testid="vehicle-availability" />
            </div>
            <p className="mt-1 text-xs text-muted">All tiers are proposed and non-binding until definitive agreements and legal approval.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {v.tiers.map((t) => (
                <div key={t.funding_pct} className="rounded-xl border border-black/[0.08] bg-surface/50 p-4 text-center" data-testid={`tier-${t.funding_pct}`}>
                  <p className="font-display text-xl font-extrabold text-offwhite">{t.funding_pct}%</p>
                  <p className="font-mono text-sm text-muted">{money(t.amount)}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-muted">{t.profit_pct}% of Defined Vehicle Profit</p>
                  <button onClick={() => indicate(t.funding_pct)} disabled={v.availability !== "open"}
                    className="mt-3 w-full rounded-full bg-red-700 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-red-600 disabled:opacity-40"
                    data-testid={`indicate-interest-${t.funding_pct}`}>
                    Indicate Interest
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-3 rounded bg-surface/70 p-3 text-[11px] leading-relaxed text-muted">{v.defined_vehicle_profit_explanation}</p>
          </div>

          {/* status grid */}
          <div className="card-line rounded-xl bg-white p-6" data-testid="vehicle-statuses">
            <h2 className="font-display text-base font-extrabold text-offwhite">Diligence status</h2>
            <div className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {statusRows.map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-3 border-b border-black/[0.05] pb-2 text-xs">
                  <span className="text-muted">{label}</span>
                  <span className="text-right font-semibold text-offwhite">{value || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* milestones */}
          <div className="card-line rounded-xl bg-white p-6" data-testid="vehicle-milestones">
            <h2 className="font-display text-base font-extrabold text-offwhite">Deployment milestones</h2>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {v.milestones.map((mstone) => (
                <div key={mstone.gate} className="flex items-center gap-2.5 text-xs" data-testid={`milestone-${mstone.gate}`}>
                  {mstone.done ? <CheckCircle2 size={15} className="shrink-0 text-emerald-600" /> : <Circle size={15} className="shrink-0 text-black/20" />}
                  <span className={mstone.done ? "font-semibold text-offwhite" : "text-muted"}>{mstone.label}</span>
                  {mstone.date && <span className="ml-auto font-mono text-[10px] text-muted">{mstone.date}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* risk factors */}
          <div className="rounded-xl border border-red-500/25 bg-white p-6" data-testid="vehicle-risks">
            <h2 className="font-display text-base font-extrabold text-red-800">Risk factors</h2>
            <ul className="mt-3 space-y-2">
              {v.risk_factors.map((r) => (
                <li key={r.slice(0, 30)} className="flex gap-2.5 text-xs leading-relaxed text-muted">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-600" /> {r}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-6">
          {/* insurance */}
          <div className="card-line rounded-xl bg-white p-5" data-testid="vehicle-insurance">
            <h2 className="flex items-center gap-2 font-display text-sm font-extrabold text-offwhite"><ShieldCheck size={14} className="text-red-700" /> Insurance</h2>
            <dl className="mt-3 space-y-2 text-xs">
              {[
                ["Carrier", v.insurance?.carrier],
                ["Policy number", v.insurance?.policy_number || "Pending"],
                ["Effective", v.insurance?.effective_date || "—"],
                ["Expiration", v.insurance?.expiration_date || "—"],
                ["Liability limit", v.insurance?.liability_limit ? money(v.insurance.liability_limit) : "—"],
                ["Physical damage limit", v.insurance?.physical_damage_limit ? money(v.insurance.physical_damage_limit) : "—"],
                ["Deductible", v.insurance?.deductible ? money(v.insurance.deductible) : "—"],
                ["Endorsement status", v.insurance?.endorsement_status],
                ["Partner deductible contribution", money(v.insurance?.partner_deductible_contribution ?? 2500)],
                ["XE deductible balance", v.insurance?.xe_deductible_balance],
                ["Claim status", v.insurance?.claim_status],
              ].map(([k, val]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-black/[0.05] pb-1.5">
                  <dt className="text-muted">{k}</dt><dd className="text-right font-semibold text-offwhite">{val ?? "—"}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-[10px] leading-relaxed text-muted">
              Fleet partners contribute 45% of any deductible, not exceeding $2,500; Xtreme Exotics covers the balance.
            </p>
          </div>

          {/* sources & uses */}
          <div className="card-line rounded-xl bg-white p-5" data-testid="vehicle-sources-uses">
            <h2 className="font-display text-sm font-extrabold text-offwhite">Sources & uses</h2>
            <div className="mt-3 space-y-2 text-xs">
              {(v.sources_and_uses || []).map((s) => (
                <div key={s.use} className="flex justify-between gap-3 border-b border-black/[0.05] pb-1.5">
                  <span className="text-muted">{s.use}</span>
                  <span className="font-semibold text-offwhite">{typeof s.amount === "number" ? money(s.amount) : s.amount}</span>
                </div>
              ))}
            </div>
          </div>

          {/* approved documents */}
          <div className="card-line rounded-xl bg-white p-5" data-testid="vehicle-documents">
            <h2 className="flex items-center gap-2 font-display text-sm font-extrabold text-offwhite"><FileText size={14} className="text-red-700" /> Related documents</h2>
            <div className="mt-3 space-y-2">
              {!v.documents.length && <p className="text-xs text-muted">No documents published for your access level yet.</p>}
              {v.documents.map((d) => (
                <Link key={d.document_id} to="/investors/portal/documents" className="flex items-center justify-between rounded-lg border border-black/[0.08] p-2.5 text-xs hover:border-red-700/40" data-testid={`vehicle-doc-${d.document_id}`}>
                  <span className="font-semibold text-offwhite">{d.title}</span>
                  <span className="font-mono text-[10px] text-muted">v{d.version}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </InvestorShell>
  );
}
